// ────────────────────────────────────────────────────────────────────────────
// Year-rhythm synthesis (Slice 10).
//
// The projection engine is sub-annually aware (Slice 4 — time-weighted growth
// from `ContributionSchedule` kinds: monthly, lump_sum, with_bonus,
// with_paycheck), but the chart is purely annual. Bonuses paid in February,
// January 401(k) lumps, and monthly brokerage streams all collapse into single
// year-end data points.
//
// This module turns a (Compensation, ContributionRule[]) pair into a small set
// of marker shapes the `<YearRhythmStrip>` widget renders on a 12-month track:
//
//   • inflows   — discrete deposits into the household (bonus, RSU vest,
//                 employer match — the match shape mirrors the matched rule)
//   • outflows  — discrete one-shot contributions (lump_sum or with_bonus)
//   • bands     — continuous monthly streams (monthly, with_paycheck) and
//                 mandatory monthly outflows (household spend, mortgage P&I,
//                 escrow, healthcare/12, active-loan P&I)
//
// Pure data; no React. Tested directly in `year-rhythm.test.ts`.
// ────────────────────────────────────────────────────────────────────────────

import type {
  Compensation,
  ContributionRule,
  PayMonth,
} from '../types';
import {
  computeEmployerMatch,
  resolveRuleAnnual,
} from '../engine/contributions';

export type RhythmInflowKind = 'bonus' | 'rsu' | 'employer_match';
export type RhythmOutflowKind = 'contribution_lump' | 'contribution_bonus';
export type RhythmBandKind =
  | 'contribution_monthly'
  | 'spend'
  | 'mortgage_pi'
  | 'escrow'
  | 'healthcare'
  | 'loan_pi';

/** Discrete one-shot inflow on a specific month. */
export interface RhythmInflow {
  id: string;
  kind: RhythmInflowKind;
  month: PayMonth;
  amount: number;
  label: string;
  /** Optional rule id — clicking the marker can deep-link to its editor. */
  ruleId?: string;
}

/** Discrete one-shot outflow / saved contribution on a specific month. */
export interface RhythmOutflow {
  id: string;
  kind: RhythmOutflowKind;
  month: PayMonth;
  amount: number;
  label: string;
  ruleId?: string;
}

/** Continuous monthly stream (rendered as a horizontal band across all 12). */
export interface RhythmBand {
  id: string;
  kind: RhythmBandKind;
  /** Per-month amount in today's dollars. */
  monthlyAmount: number;
  label: string;
  ruleId?: string;
}

/** Year-rhythm summary for ONE projection year. */
export interface YearRhythm {
  yearOffset: number;
  calendarYear: number;
  inflows: RhythmInflow[];
  outflows: RhythmOutflow[];
  bands: RhythmBand[];
  /** Net cash surplus from the budget reconciler if available. */
  netCashSurplus?: number;
}

/** Inputs needed to synthesize one year's rhythm. */
export interface YearRhythmInput {
  yearOffset: number;
  calendarYear: number;
  compensation: Compensation | undefined;
  rules: ContributionRule[];
  /** Mandatory monthly outflows (today's $). */
  monthlyHouseholdSpend: number;
  monthlyMortgagePI: number;
  monthlyEscrow: number;
  /** Annual healthcare expense, divided by 12 to render as a band. */
  annualHealthcare: number;
  /** Active non-mortgage loans this year — surfaced as bands when present. */
  activeLoans?: { id: string; label: string; monthlyPI: number }[];
  /** Engine's per-year `budget.netCashSurplus` if known. */
  netCashSurplus?: number;
}

/** Synthesize a year's rhythm: inflows, outflows, and continuous bands. */
export function buildYearRhythm(input: YearRhythmInput): YearRhythm {
  const inflows: RhythmInflow[] = [];
  const outflows: RhythmOutflow[] = [];
  const bands: RhythmBand[] = [];

  const comp = input.compensation;
  const bonusMonth: PayMonth = comp?.bonusMonth ?? 2;

  // ── Inflows ────────────────────────────────────────────────────────────
  if (comp && comp.bonusAnnual > 0) {
    inflows.push({
      id: `bonus-${input.yearOffset}`,
      kind: 'bonus',
      month: bonusMonth,
      amount: comp.bonusAnnual,
      label: 'Bonus',
    });
  }

  // RSU vests — match the year by `yearOffset`.
  if (comp?.rsuVests) {
    for (const vest of comp.rsuVests) {
      if (vest.yearOffset !== input.yearOffset) continue;
      if (vest.grossValue <= 0) continue;
      inflows.push({
        id: `rsu-${input.yearOffset}-${vest.month}`,
        kind: 'rsu',
        month: vest.month,
        amount: vest.grossValue,
        label: 'RSU vest',
      });
    }
  }

  // Employer 401(k) match — distributes across the year matching the shape
  // of the EMPLOYEE deferral. If the employee deferral is a lump_sum, the
  // match lumps too. If monthly, the match streams. If multiple deferral
  // rules with mixed schedules, we attribute the match proportionally.
  if (comp && (comp.employer401kMatchPct ?? 0) > 0 && (comp.employer401kMatchCapPct ?? 0) > 0) {
    // Sum employee 401(k) deferrals (pretax + roth_401k) — the engine's match
    // formula is total-employee-driven, so we only need to know the total to
    // compute the year's match dollars.
    const employeeDeferralRules = input.rules.filter(
      r =>
        r.enabled &&
        (r.taxTreatment === 'pretax' || r.taxTreatment === 'roth_401k'),
    );
    const totalEmployee = employeeDeferralRules.reduce(
      (s, r) => s + resolveRuleAnnual(r, comp),
      0,
    );
    const matchAnnual = computeEmployerMatch(comp, totalEmployee);
    if (matchAnnual > 0 && totalEmployee > 0) {
      // Attribute the match across rules in proportion to each rule's share
      // of the total employee deferral, then surface as inflow markers
      // following each contributing rule's schedule.
      for (const rule of employeeDeferralRules) {
        const share = resolveRuleAnnual(rule, comp) / totalEmployee;
        const ruleMatch = matchAnnual * share;
        if (ruleMatch <= 0) continue;
        const sched = rule.schedule;
        if (sched.kind === 'lump_sum') {
          inflows.push({
            id: `match-${rule.id}`,
            kind: 'employer_match',
            month: sched.month,
            amount: ruleMatch,
            label: `Employer match`,
            ruleId: rule.id,
          });
        } else if (sched.kind === 'with_bonus') {
          inflows.push({
            id: `match-${rule.id}`,
            kind: 'employer_match',
            month: bonusMonth,
            amount: ruleMatch,
            label: `Employer match`,
            ruleId: rule.id,
          });
        } else {
          // monthly / with_paycheck — render as a continuous band.
          bands.push({
            id: `match-${rule.id}`,
            kind: 'contribution_monthly',
            monthlyAmount: ruleMatch / 12,
            label: 'Employer match',
            ruleId: rule.id,
          });
        }
      }
    }
  }

  // ── Contribution rules → outflows or bands ─────────────────────────────
  if (comp) {
    for (const rule of input.rules) {
      if (!rule.enabled) continue;
      const annual = resolveRuleAnnual(rule, comp);
      if (annual <= 0) continue;
      const sched = rule.schedule;
      if (sched.kind === 'lump_sum') {
        outflows.push({
          id: `c-${rule.id}`,
          kind: 'contribution_lump',
          month: sched.month,
          amount: annual,
          label: rule.name,
          ruleId: rule.id,
        });
      } else if (sched.kind === 'with_bonus') {
        outflows.push({
          id: `c-${rule.id}`,
          kind: 'contribution_bonus',
          month: bonusMonth,
          amount: annual,
          label: rule.name,
          ruleId: rule.id,
        });
      } else {
        // monthly / with_paycheck
        bands.push({
          id: `c-${rule.id}`,
          kind: 'contribution_monthly',
          monthlyAmount: annual / 12,
          label: rule.name,
          ruleId: rule.id,
        });
      }
    }
  }

  // ── Mandatory monthly outflow bands ───────────────────────────────────
  if (input.monthlyHouseholdSpend > 0) {
    bands.push({
      id: 'household-spend',
      kind: 'spend',
      monthlyAmount: input.monthlyHouseholdSpend,
      label: 'Household spend',
    });
  }
  if (input.monthlyMortgagePI > 0) {
    bands.push({
      id: 'mortgage-pi',
      kind: 'mortgage_pi',
      monthlyAmount: input.monthlyMortgagePI,
      label: 'Mortgage P&I',
    });
  }
  if (input.monthlyEscrow > 0) {
    bands.push({
      id: 'mortgage-escrow',
      kind: 'escrow',
      monthlyAmount: input.monthlyEscrow,
      label: 'Escrow (taxes & ins.)',
    });
  }
  if (input.annualHealthcare > 0) {
    bands.push({
      id: 'healthcare',
      kind: 'healthcare',
      monthlyAmount: input.annualHealthcare / 12,
      label: 'Healthcare',
    });
  }
  for (const loan of input.activeLoans ?? []) {
    if (loan.monthlyPI <= 0) continue;
    bands.push({
      id: `loan-${loan.id}`,
      kind: 'loan_pi',
      monthlyAmount: loan.monthlyPI,
      label: `${loan.label} P&I`,
    });
  }

  // Stable ordering so the strip doesn't shuffle on re-render.
  inflows.sort((a, b) => a.month - b.month || a.id.localeCompare(b.id));
  outflows.sort((a, b) => a.month - b.month || a.id.localeCompare(b.id));
  bands.sort((a, b) => bandSortKey(a) - bandSortKey(b));

  return {
    yearOffset: input.yearOffset,
    calendarYear: input.calendarYear,
    inflows,
    outflows,
    bands,
    netCashSurplus: input.netCashSurplus,
  };
}

/** Sort bands so contributions appear first, then mandatory outflows in a
 *  stable order — keeps the strip's bottom rows readable across years. */
function bandSortKey(b: RhythmBand): number {
  switch (b.kind) {
    case 'contribution_monthly':
      return 0;
    case 'spend':
      return 1;
    case 'mortgage_pi':
      return 2;
    case 'escrow':
      return 3;
    case 'healthcare':
      return 4;
    case 'loan_pi':
      return 5;
  }
}

/** Convenience: total annual inflows + outflows for the strip's summary. */
export function rhythmTotals(r: YearRhythm): {
  totalInflows: number;
  totalOutflows: number;
  totalBandsAnnual: number;
} {
  const totalInflows = r.inflows.reduce((s, x) => s + x.amount, 0);
  const totalOutflows = r.outflows.reduce((s, x) => s + x.amount, 0);
  const totalBandsAnnual = r.bands.reduce((s, x) => s + x.monthlyAmount * 12, 0);
  return { totalInflows, totalOutflows, totalBandsAnnual };
}

/** Annual cash-flow totals — the bottom card on `<YearRhythmStrip>`.
 *  Combines `<YearRhythm>` (which already knows gross inflows + monthly bands)
 *  with the engine's per-year `YearBudget` (which already knows the after-tax
 *  reconciliation). Discretionary out is split into "saved" (monthly
 *  contribution bands) vs. "cash_flow events" (purchases / gifts paid out of
 *  take-home). All values are in today's dollars. */
export interface YearAnnualTotals {
  /** Gross inflows: base salary + bonus + employer match + RSU vests. */
  inflows: number;
  /** After-tax cash actually arriving in the user's hands. From
   *  `YearBudget.cashIn`. */
  afterTax: number;
  /** Mandatory monthly outflows annualized: spend + mortgage + escrow +
   *  healthcare + active-loan P&I. From the band rows. */
  mandatoryOut: number;
  /** Discretionary outflows the user authored: monthly contribution bands
   *  routed into the `taxable` / `hysa` / `retirement` buckets, plus any
   *  with-bonus / lump-sum contributions, plus cash_flow purchases. */
  discretionaryOut: number;
  /** Net cash position at year-end, mirrored from the engine's budget. */
  netCash: number;
}

/** Inputs for `computeYearAnnualTotals`. */
export interface YearAnnualTotalsInput {
  rhythm: YearRhythm;
  /** Compensation block — base salary anchors the gross-inflows row. */
  baseAnnual: number;
  /** Per-year budget block (from `ProjectionYear.budget`) — supplies
   *  after-tax cash, discretionary cash_flow outflows, and the net surplus
   *  reconciled by the engine. */
  budget: {
    cashIn: number;
    discretionaryOut: number;
    netCashSurplus: number;
  } | undefined;
}

/** Reduce a year's rhythm + engine budget into the five-row Annual Totals
 *  card. Pure data; the renderer just formats. */
export function computeYearAnnualTotals(input: YearAnnualTotalsInput): YearAnnualTotals {
  const { rhythm, baseAnnual, budget } = input;

  // Gross inflows = base + bonus + RSU vests + employer match. The rhythm's
  // inflows array already covers bonus / RSU / lump-sum employer_match;
  // employer_match that streams monthly is in the bands.
  const employerMatchBandAnnual = rhythm.bands
    .filter(b => b.kind === 'contribution_monthly' && b.label === 'Employer match')
    .reduce((s, b) => s + b.monthlyAmount * 12, 0);
  const inflowsTotal =
    baseAnnual +
    rhythm.inflows.reduce((s, x) => s + x.amount, 0) +
    employerMatchBandAnnual;

  // Mandatory bands: everything that isn't a contribution.
  const mandatoryAnnual = rhythm.bands
    .filter(b => b.kind !== 'contribution_monthly')
    .reduce((s, b) => s + b.monthlyAmount * 12, 0);

  // Discretionary "saved": monthly contribution bands (excluding employer
  // match, which is not the user's cash) + with-bonus / lump contributions.
  const userMonthlyContribAnnual = rhythm.bands
    .filter(b => b.kind === 'contribution_monthly' && b.label !== 'Employer match')
    .reduce((s, b) => s + b.monthlyAmount * 12, 0);
  const oneShotContribTotal = rhythm.outflows.reduce((s, x) => s + x.amount, 0);
  const userSavedAnnual = userMonthlyContribAnnual + oneShotContribTotal;

  // Cash-flow purchases / gifts come from the engine. We add them to
  // discretionary so the totals reconcile against `netCashSurplus`.
  const cashFlowSpend = Math.max(0, budget?.discretionaryOut ?? 0);

  return {
    inflows: inflowsTotal,
    afterTax: Math.max(0, budget?.cashIn ?? 0),
    mandatoryOut: mandatoryAnnual,
    discretionaryOut: userSavedAnnual + cashFlowSpend,
    netCash: budget?.netCashSurplus ?? 0,
  };
}
